import { create } from "zustand";
import { UserSessionLog } from "@/types";
import { supabase } from "@/lib/supabase";

const SESSION_LOG_RETENTION_DAYS = 30;

interface SessionLogsState {
  logs: UserSessionLog[];
  loading: boolean;
  error: string | null;
  fetchLogs: () => Promise<void>;
  clearLogs: () => void;
}

export const useSessionLogsStore = create<SessionLogsState>((set) => ({
  logs: [],
  loading: false,
  error: null,
  fetchLogs: async () => {
    set({ loading: true, error: null });
    const retentionCutoff = new Date(
      Date.now() - SESSION_LOG_RETENTION_DAYS * 24 * 60 * 60 * 1000
    ).toISOString();
    // First try selecting with user_email (new column). If it fails, fall back without it.
    const { data, error } = await supabase
      .from("user_session_logs")
      .select("id, user_id, user_name, user_role, action, details, timestamp, ip_address, device_info")
      .gte("timestamp", retentionCutoff)
      .order("timestamp", { ascending: false });
    if (error) {
      // Fallback: re-run without user_email for older schema
      const { data: dataFallback, error: errorFallback } = await supabase
        .from("user_session_logs")
        .select("id, user_id, user_name, user_role, action, details, timestamp, ip_address, device_info")
        .gte("timestamp", retentionCutoff)
        .order("timestamp", { ascending: false });
      if (errorFallback) {
        set({ error: errorFallback.message, loading: false });
      } else {
        set({
          logs: (dataFallback || []).map((log: any) => ({
            id: log.id,
            userId: log.user_id,
            userName: log.user_name,
            userRole: log.user_role,
            action: log.action,
            details: log.details,
            timestamp: log.timestamp,
            ipAddress: log.ip_address,
            deviceInfo: log.device_info,
          })),
          loading: false,
          error: null,
        });
      }
    } else {
      // Map Supabase fields to UserSessionLog type
      set({
        logs: (data || []).map((log: any) => ({
          id: log.id,
          userId: log.user_id,
          userEmail: log.user_email,
          userName: log.user_name,
          userRole: log.user_role,
          action: log.action,
          details: log.details,
          timestamp: log.timestamp,
          ipAddress: log.ip_address,
          deviceInfo: log.device_info,
        })),
        loading: false,
        error: null,
      });
    }
  },
  clearLogs: () => {
    set({ logs: [], error: null });
  },
})); 